import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useClientTranslation } from '@/lib/core';
import { SpinLoading } from '@/components/shared/loadings/spin';

type ConfirmationStatus = 'idle' | 'loading' | 'success' | 'error';

export interface ConfirmationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  onSuccess?: () => void;
  headerTitle?: string;
  title?: string;
  description?: string;
  confirmLabel?: string;
  dismissLabel?: string;
  successText?: string;
  errorText?: string;
  silentSuccess?: boolean;
}

export function ConfirmationSheet({
  open,
  onOpenChange,
  onConfirm,
  onSuccess,
  headerTitle,
  title,
  description,
  confirmLabel,
  dismissLabel,
  successText,
  errorText,
  silentSuccess = false,
}: ConfirmationSheetProps) {
  const translation = useClientTranslation();

  const [status, setStatus] = useState<ConfirmationStatus>('idle');

  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => setStatus('idle'), 300);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (status !== 'success' && status !== 'error') return;
    const timer = setTimeout(() => {
      onOpenChange(false);
      if (status === 'success') setTimeout(() => onSuccess?.(), 300);
    }, 1500);
    return () => clearTimeout(timer);
  }, [status, onOpenChange, onSuccess]);

  async function handleConfirm() {
    setStatus('loading');
    try {
      await onConfirm();
      if (silentSuccess) {
        onOpenChange(false);
        setTimeout(() => onSuccess?.(), 300);
      } else {
        setStatus('success');
      }
    } catch {
      setStatus('error');
    }
  }

  const isResult = status === 'success' || status === 'error';

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle={false}>
      <DrawerContent>
        {status === 'loading' ? (
          <SpinLoading
            wrapperClassName="min-h-0 flex-1 my-8"
            spinnerClassName="border-jncquoi-neutral-1000"
          />
        ) : isResult ? (
          <div className="animate-sheet-result-in flex items-center gap-2 px-4 py-8">
            {status === 'success' ? (
              <>
                <Icon
                  name="check-circle-solid"
                  width={16}
                  height={16}
                  className="text-jncquoi-neutral-1000 shrink-0"
                />
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                  {successText ?? translation('App.Labels.ConfirmationSuccess')}
                </p>
              </>
            ) : (
              <>
                <Icon
                  name="warning-circle"
                  width={16}
                  height={16}
                  className="text-jncquoi-neutral-1000 shrink-0"
                />
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                  {errorText ?? translation('App.Labels.ConfirmationError')}
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <DrawerHeader className="py-8">
              {headerTitle && (
                <p className="font-pp-neue-montreal pb-6 text-center text-[14px] tracking-[0.5px] text-[#1a1a1a] uppercase">
                  {headerTitle}
                </p>
              )}
              <DrawerTitle>{title ?? translation('App.Labels.ConfirmationTitle')}</DrawerTitle>
              {/*
                Left-aligned per the design — only the `CONFIRMATION` label above is centred.
                `leading-normal` overrides the primitive's `leading-none`, which would jam the two
                lines of a wrapping description together.
              */}
              {description && (
                <DrawerDescription className="leading-normal">
                  {description ?? translation('App.Labels.ConfirmationText')}
                </DrawerDescription>
              )}
            </DrawerHeader>
            <DrawerFooter>
              <Button variant="primary-solid" palette="dark" size="md" onClick={handleConfirm}>
                {confirmLabel ?? translation('App.Labels.Confirm')}
              </Button>
              <DrawerClose
                render={
                  <Button variant="tertiary-link" palette="dark" size="sm">
                    {dismissLabel ?? translation('App.Labels.Dismiss')}
                  </Button>
                }
              />
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
